import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    Building2,
    Briefcase,
    CalendarDays,
    CheckCircle2,
    ChevronRight,
    Globe,
    HeartPulse,
    LayoutDashboard,
    Landmark,
    Laptop,
    Rocket,
    Search,
    ShieldCheck,
    Sparkles,
    Users,
    Wand2
} from "lucide-react";
import "./LandingPage.css";

const stats = [
    { label: "Industry-ready layouts", value: "5+" },
    { label: "Core business flows", value: "CRM + Web" },
    { label: "Launch speed", value: "Minutes" },
    { label: "Output", value: "Multi-page" }
];

const problems = [
    "Hiring developers is expensive and slow",
    "Templates rarely match your business model",
    "Most website tools stop at design only",
    "Managing leads, teams, and content becomes fragmented"
];

const solutions = [
    "Generate full multi-page websites with AI",
    "Use layouts tuned for real business industries",
    "Run website and CRM from one connected platform",
    "Go live faster with structured, scalable output"
];

const steps = [
    {
        title: "Enter your business details",
        description:
            "Add your business name, industry, services, goals, and style preferences to guide the generation flow.",
        icon: Briefcase
    },
    {
        title: "AI builds your website",
        description:
            "Shriansh Nexus creates a structured, industry-aware, multi-page website with sections built for conversion.",
        icon: Wand2
    },
    {
        title: "Manage everything in CRM",
        description:
            "Handle projects, leads, users, and future business operations from the same workspace.",
        icon: LayoutDashboard
    }
];

const industries = [
    {
        title: "Real Estate",
        description: "Property-focused layouts, lead capture, locality sections, listing-style flows.",
        icon: Building2,
        href: "https://sites.shrianshnexus.in/veloura-studio/index.html",
        badge: "Lead-first"
    },
    {
        title: "Academy",
        description: "Programs, coaches, admissions, showcase sections, and trust-first presentation.",
        icon: Users,
        href: "#",
        badge: "Program-first"
    },
    {
        title: "Healthcare",
        description: "Clean appointment-ready pages for clinics, services, doctors, and patient trust.",
        icon: HeartPulse,
        href: "https://sites.shrianshnexus.in/carenest1-clinic/index.html",
        badge: "Trust-first"
    },
    {
        title: "Legal",
        description: "Consultation-ready firm websites with authority, credibility, and service clarity.",
        icon: Landmark,
        href: "https://sites.shrianshnexus.in/lexcore1-advisors/index.html",
        badge: "Authority-first"
    },
    {
        title: "SaaS / Tech",
        description: "Modern product layouts, feature storytelling, onboarding flows, and conversion sections.",
        icon: Laptop,
        href: "https://sites.shrianshnexus.in/flowpilot1-ai/index.html",
        badge: "Conversion-first"
    }
];

const features = [
    {
        title: "AI Website Generation",
        description: "Generate business websites faster with structured AI-assisted content and layouts.",
        icon: Sparkles
    },
    {
        title: "Multi-page Output",
        description: "Home, about, services, programs, contact, and other pages generated in one flow.",
        icon: Globe
    },
    {
        title: "Industry-aware Design",
        description: "Website structure adapts to the business type instead of forcing generic templates.",
        icon: Search
    },
    {
        title: "Built-in CRM",
        description: "Move from website creation to operations, management, and long-term platform usage.",
        icon: LayoutDashboard
    },
    {
        title: "SEO-ready Structure",
        description: "Landing architecture, content sections, and page hierarchy designed for search visibility.",
        icon: Rocket
    },
    {
        title: "Scalable Foundation",
        description: "Perfect for future workflows like admin roles, industry modules, and business tooling.",
        icon: ShieldCheck
    }
];

const faqs = [
    {
        q: "What is Shriansh Nexus?",
        a: "Shriansh Nexus is an AI-powered website generation platform with a connected CRM layer, built to help businesses launch and manage their digital presence faster."
    },
    {
        q: "Is this only a website builder?",
        a: "No. The long-term value is not just page generation. Shriansh Nexus is designed to evolve into a business operating platform with industry-specific workflows."
    },
    {
        q: "Which industries does it support?",
        a: "It is being shaped for real estate, academies, healthcare, legal, SaaS, and other service-driven businesses that need more than a generic template."
    },
    {
        q: "Where do users log in?",
        a: "Users sign in through admin.shrianshnexus.in, where they can access generation, projects, dashboards, and CRM capabilities."
    }
];

function SectionHeading({ eyebrow, title, subtitle }) {
    return (
        <div className="landing-section-heading">
            <div className="landing-eyebrow">
                <Sparkles size={16} />
                <span>{eyebrow}</span>
            </div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
        </div>
    );
}

function GlassCard({ className = "", children }) {
    return <div className={`landing-card ${className}`}>{children}</div>;
}

export default function LandingPage() {
    const navigate = useNavigate();
    const [businessName, setBusinessName] = useState("");
    const [industry, setIndustry] = useState("");
    const [previewStarting, setPreviewStarting] = useState(false);

    const handleGeneratePreview = () => {
        const cleanBusinessName = businessName.trim();
        const cleanIndustry = industry.trim();

        if (!cleanBusinessName || !cleanIndustry) {
            alert("Please enter business name and select industry.");
            return;
        }

        setPreviewStarting(true);

        const query = new URLSearchParams({
            name: cleanBusinessName,
            industry: cleanIndustry
        }).toString();

        setTimeout(() => {
            navigate(`/generate?${query}`, {
                state: {
                    previewSeed: {
                        businessName: cleanBusinessName,
                        industry: cleanIndustry,
                        autoGenerate: true
                    }
                }
            });
        }, 500);
    };

    return (
        <div className="landing-page">
            <div className="landing-bg">
                <div className="landing-orb landing-orb-1" />
                <div className="landing-orb landing-orb-2" />
                <div className="landing-orb landing-orb-3" />
                <div className="landing-grid" />
            </div>

            <header className="landing-header">
                <div className="landing-container landing-header-inner">
                    <div className="landing-brand">
                        <div className="landing-brand-logo">
                            <Sparkles size={20} />
                        </div>
                        <div>
                            <div className="landing-brand-title">Shriansh Nexus</div>
                            <div className="landing-brand-subtitle">Built by Shriansh AI</div>
                        </div>
                    </div>

                    <nav className="landing-nav">
                        <a href="#how-it-works">How it works</a>
                        <a href="#industries">Industries</a>
                        <a href="#features">Features</a>
                        <a href="#seo">Why Nexus</a>
                        <a href="#faq">FAQ</a>
                    </nav>

                    <div className="landing-header-actions">
                        <a
                            className="landing-btn landing-btn-ghost"
                            href="https://admin.shrianshnexus.in/login?entry=public"
                        >
                            Login
                        </a>
                        <a
                            className="landing-btn landing-btn-primary"
                            href="https://admin.shrianshnexus.in/signup?entry=public"
                        >
                            Start Free
                            <ArrowRight size={16} />
                        </a>
                    </div>
                </div>
            </header>

            <main>
                <section className="landing-hero">
                    <div className="landing-container landing-hero-grid">
                        <div className="landing-hero-content">
                            <div className="landing-pill">
                                <Rocket size={16} />
                                <span>AI Website Generator + Built-in CRM</span>
                            </div>

                            <h1>Build your business website and CRM in minutes with AI.</h1>

                            <p className="landing-hero-subtext">
                                Shriansh Nexus generates industry-ready websites and gives you a
                                powerful dashboard to manage projects, leads, users, and future
                                business operations — all in one platform.
                            </p>

                            <div className="landing-input-inline">
                                <input
                                    placeholder="Business Name (e.g. Urban Realty)"
                                    value={businessName}
                                    onChange={(e) => setBusinessName(e.target.value)}
                                    className="landing-input"
                                />

                                <input
                                    placeholder="Industry (e.g. Real Estate)"
                                    value={industry}
                                    onChange={(e) => setIndustry(e.target.value)}
                                    className="landing-input"
                                />
                            </div>

                            <div className="landing-hero-actions">
                                <a
                                    className="landing-btn landing-btn-primary"
                                    href="https://admin.shrianshnexus.in/signup?entry=public"
                                >
                                    Start Free
                                    <ArrowRight size={16} />
                                </a>

                                <button
                                    type="button"
                                    className="landing-btn landing-btn-secondary"
                                    onClick={handleGeneratePreview}
                                    disabled={previewStarting}
                                >
                                    {previewStarting ? "Preparing Preview..." : "Generate Website"}
                                    {!previewStarting && <Wand2 size={16} />}
                                </button>

                                <a
                                    className="landing-btn landing-btn-secondary"
                                    href="https://admin.shrianshnexus.in/login?entry=public"
                                >
                                    Request Demo
                                    <CalendarDays size={16} />
                                </a>
                            </div>

                            <div className="landing-trust-list">
                                <div className="landing-trust-item">
                                    <CheckCircle2 size={18} />
                                    <span>Built by Shriansh AI</span>
                                </div>
                                <div className="landing-trust-item">
                                    <CheckCircle2 size={18} />
                                    <span>Multi-page output</span>
                                </div>
                                <div className="landing-trust-item">
                                    <CheckCircle2 size={18} />
                                    <span>CRM-ready workflow</span>
                                </div>
                            </div>
                        </div>

                        <div className="landing-dashboard-wrap">
                            <GlassCard className="landing-dashboard-shell">
                                <div className="landing-dashboard-top">
                                    <div>
                                        <div className="landing-muted">Workspace</div>
                                        <div className="landing-dashboard-title">
                                            Shriansh Nexus Dashboard
                                        </div>
                                    </div>
                                    <div className="landing-badge">Live preview ready</div>
                                </div>

                                <div className="landing-dashboard-grid">
                                    <GlassCard className="landing-dashboard-panel">
                                        <div className="landing-panel-title">
                                            <Wand2 size={16} />
                                            <span>Website Generation Flow</span>
                                        </div>

                                        <div className="landing-flow-list">
                                            {[
                                                "Business profile",
                                                "Industry-aware layout",
                                                "Page generation",
                                                "Deployment-ready output"
                                            ].map((item) => (
                                                <div className="landing-flow-item" key={item}>
                                                    <span>{item}</span>
                                                    <span className="landing-dot" />
                                                </div>
                                            ))}
                                        </div>
                                    </GlassCard>

                                    <div className="landing-dashboard-side">
                                        <GlassCard className="landing-dashboard-panel">
                                            <div className="landing-muted">Selected industry</div>
                                            <div className="landing-panel-heading">Real Estate</div>
                                            <div className="landing-highlight-box">
                                                Property-led structure with locality sections, trust
                                                blocks, service pages, and conversion-focused contact flow.
                                            </div>
                                        </GlassCard>

                                        <GlassCard className="landing-dashboard-panel">
                                            <div className="landing-panel-top-row">
                                                <div>
                                                    <div className="landing-muted">CRM snapshot</div>
                                                    <div className="landing-panel-heading">
                                                        Operations overview
                                                    </div>
                                                </div>
                                                <LayoutDashboard size={18} />
                                            </div>

                                            <div className="landing-mini-stats">
                                                {[
                                                    { label: "Projects", value: "12" },
                                                    { label: "Leads", value: "184" },
                                                    { label: "Users", value: "09" },
                                                    { label: "Pages", value: "46" }
                                                ].map((item) => (
                                                    <div key={item.label} className="landing-mini-stat">
                                                        <div className="landing-mini-label">{item.label}</div>
                                                        <div className="landing-mini-value">{item.value}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </GlassCard>
                                    </div>
                                </div>
                            </GlassCard>
                        </div>
                    </div>

                    <div className="landing-container">
                        <div className="landing-stats-grid">
                            {stats.map((item) => (
                                <GlassCard key={item.label}>
                                    <div className="landing-mini-label">{item.label}</div>
                                    <div className="landing-stat-value">{item.value}</div>
                                </GlassCard>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="landing-section">
                    <div className="landing-container">
                        <div className="landing-two-col">
                            <GlassCard className="landing-problem-card">
                                <div className="landing-tag landing-tag-red">
                                    <Briefcase size={16} />
                                    <span>The old way</span>
                                </div>
                                <h3>Building a website shouldn’t take weeks.</h3>

                                <div className="landing-list-block">
                                    {problems.map((item) => (
                                        <div className="landing-list-row" key={item}>
                                            <span className="landing-red-dot" />
                                            <p>{item}</p>
                                        </div>
                                    ))}
                                </div>
                            </GlassCard>

                            <GlassCard>
                                <div className="landing-tag landing-tag-cyan">
                                    <Sparkles size={16} />
                                    <span>The Nexus way</span>
                                </div>
                                <h3>Shriansh Nexus solves this in minutes.</h3>

                                <div className="landing-list-block">
                                    {solutions.map((item) => (
                                        <div className="landing-list-row" key={item}>
                                            <CheckCircle2 size={18} className="landing-icon-cyan" />
                                            <p>{item}</p>
                                        </div>
                                    ))}
                                </div>
                            </GlassCard>
                        </div>
                    </div>
                </section>

                <section id="how-it-works" className="landing-section">
                    <div className="landing-container">
                        <SectionHeading
                            eyebrow="How it works"
                            title="From idea to live business system"
                            subtitle="A smooth path from business input to generated website and ongoing operations inside your CRM workspace."
                        />

                        <div className="landing-steps-grid">
                            {steps.map((step, index) => {
                                const Icon = step.icon;
                                return (
                                    <GlassCard key={step.title}>
                                        <div className="landing-step-top">
                                            <div className="landing-icon-box">
                                                <Icon size={24} />
                                            </div>
                                            <div className="landing-step-number">0{index + 1}</div>
                                        </div>
                                        <h3>{step.title}</h3>
                                        <p>{step.description}</p>
                                    </GlassCard>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section id="industries" className="landing-section">
                    <div className="landing-container">
                        <SectionHeading
                            eyebrow="Industry-ready"
                            title="Built for businesses that need more than a generic template"
                            subtitle="Shriansh Nexus is designed to adapt structure, sections, and presentation based on industry needs and business goals."
                        />

                        <div className="landing-industries-grid">
                            {industries.map((industry) => {
                                const Icon = industry.icon;
                                return (
                                    <GlassCard key={industry.title} className="landing-industry-card">
                                        <div className="landing-industry-top">
                                            <div className="landing-icon-box small">
                                                <Icon size={22} />
                                            </div>
                                            <span className="landing-industry-badge">
                                                {industry.badge}
                                            </span>
                                        </div>

                                        <h3>{industry.title}</h3>
                                        <p>{industry.description}</p>

                                        <a href={industry.href} className="landing-inline-link">
                                            View sample
                                            <ChevronRight size={16} />
                                        </a>
                                    </GlassCard>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section id="features" className="landing-section">
                    <div className="landing-container">
                        <SectionHeading
                            eyebrow="Platform capabilities"
                            title="Everything you need to launch, manage, and scale"
                            subtitle="The platform is designed to move from initial website generation to deeper business workflows without forcing you to rebuild from scratch later."
                        />

                        <div className="landing-features-grid">
                            {features.map((feature) => {
                                const Icon = feature.icon;
                                return (
                                    <GlassCard key={feature.title}>
                                        <div className="landing-feature-icon">
                                            <Icon size={20} />
                                        </div>
                                        <h3>{feature.title}</h3>
                                        <p>{feature.description}</p>
                                    </GlassCard>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section className="landing-section">
                    <div className="landing-container">
                        <div className="landing-product-preview-grid">
                            <div>
                                <div className="landing-eyebrow left">
                                    <LayoutDashboard size={16} />
                                    <span>Product preview</span>
                                </div>

                                <h2 className="landing-left-title">
                                    Your business dashboard, powered by AI.
                                </h2>

                                <p className="landing-left-subtitle">
                                    Beyond website generation, Shriansh Nexus brings your projects,
                                    users, and future CRM workflows into a connected environment
                                    designed for scale.
                                </p>

                                <div className="landing-check-list">
                                    {[
                                        "Manage websites, content, and structure from one dashboard",
                                        "Keep generation, CRM, and future operations in one product ecosystem",
                                        "Create a stronger experience for businesses that need more than a static site"
                                    ].map((item) => (
                                        <div key={item} className="landing-check-row">
                                            <CheckCircle2 size={18} className="landing-icon-cyan" />
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <GlassCard className="landing-product-shell">
                                <div className="landing-product-grid">
                                    <div className="landing-side-nav">
                                        <div className="landing-muted">Navigation</div>
                                        {[
                                            "Dashboard",
                                            "Generate",
                                            "Projects",
                                            "CRM",
                                            "Analytics",
                                            "Settings"
                                        ].map((item) => (
                                            <div className="landing-side-nav-item" key={item}>
                                                {item}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="landing-product-main">
                                        <div className="landing-highlight-box">
                                            <div className="landing-muted">Active project</div>
                                            <div className="landing-panel-heading">
                                                Business website rollout
                                            </div>
                                            <p>
                                                Structured generation, multi-page setup, and ongoing CRM
                                                activity handled from a single workspace.
                                            </p>
                                        </div>

                                        <div className="landing-product-metrics">
                                            <div className="landing-mini-stat">
                                                <div className="landing-mini-label">Conversion pages</div>
                                                <div className="landing-mini-value">08</div>
                                            </div>
                                            <div className="landing-mini-stat">
                                                <div className="landing-mini-label">Qualified leads</div>
                                                <div className="landing-mini-value">124</div>
                                            </div>
                                        </div>

                                        <div className="landing-activity-box">
                                            <div className="landing-muted">Recent activity</div>
                                            {[
                                                "Homepage generated successfully",
                                                "CRM account created for client workspace",
                                                "Industry layout updated for better conversions"
                                            ].map((item) => (
                                                <div key={item} className="landing-activity-item">
                                                    <span className="landing-dot" />
                                                    <span>{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </GlassCard>
                        </div>
                    </div>
                </section>

                <section className="landing-section">
                    <div className="landing-container">
                        <div className="landing-two-col">
                            <GlassCard>
                                <div className="landing-eyebrow left">
                                    <Wand2 size={16} />
                                    <span>Try before signup</span>
                                </div>

                                <h3>See it in action instantly</h3>
                                <p className="landing-card-subtext">
                                    Start with your business name and industry. We’ll take you to the
                                    generator with everything prefilled.
                                </p>

                                <div className="landing-form-grid">
                                    <input
                                        type="text"
                                        placeholder="Business name"
                                        value={businessName}
                                        onChange={(e) => setBusinessName(e.target.value)}
                                        className="landing-input"
                                    />

                                    <select
                                        value={industry}
                                        onChange={(e) => setIndustry(e.target.value)}
                                        className="landing-input"
                                    >
                                        <option value="">Choose industry</option>
                                        <option value="Real Estate">Real Estate</option>
                                        <option value="Academy">Academy</option>
                                        <option value="Healthcare">Healthcare</option>
                                        <option value="Legal">Legal</option>
                                        <option value="SaaS">SaaS</option>
                                    </select>
                                </div>

                                <div className="landing-form-actions">
                                    <button
                                        type="button"
                                        onClick={handleGeneratePreview}
                                        className="landing-btn landing-btn-primary"
                                    >
                                        Generate Preview
                                        <ArrowRight size={16} />
                                    </button>

                                    <a
                                        href="https://admin.shrianshnexus.in/signup?entry=public"
                                        className="landing-btn landing-btn-secondary"
                                    >
                                        Create Account
                                    </a>
                                </div>
                            </GlassCard>

                            <GlassCard id="request-demo">
                                <div className="landing-eyebrow left">
                                    <CalendarDays size={16} />
                                    <span>Request demo</span>
                                </div>

                                <h3>Want a guided walkthrough?</h3>
                                <p className="landing-card-subtext">
                                    Book a product demo to see how Shriansh Nexus can fit your
                                    business, your industry, and your growth flow.
                                </p>

                                <div className="landing-demo-form">
                                    <input
                                        type="text"
                                        placeholder="Your name"
                                        className="landing-input"
                                    />
                                    <input
                                        type="email"
                                        placeholder="Work email"
                                        className="landing-input"
                                    />
                                    <textarea
                                        rows="4"
                                        placeholder="Tell us about your business or use case"
                                        className="landing-input landing-textarea"
                                    />
                                </div>

                                <a
                                    href="mailto:hello@shriansh.ai?subject=Shriansh%20Nexus%20Demo%20Request"
                                    className="landing-btn landing-btn-light"
                                >
                                    Request Demo
                                    <ArrowRight size={16} />
                                </a>
                            </GlassCard>
                        </div>
                    </div>
                </section>

                <section id="seo" className="landing-section">
                    <div className="landing-container">
                        <SectionHeading
                            eyebrow="Why Shriansh Nexus"
                            title="More than an AI website builder"
                            subtitle="This is positioned to grow beyond page generation and become a serious digital operating layer for real businesses."
                        />

                        <div className="landing-two-col">
                            <GlassCard>
                                <h3>What is an AI website generator?</h3>
                                <div className="landing-rich-text">
                                    <p>
                                        An AI website generator helps businesses create websites faster
                                        without depending entirely on manual design and development.
                                        Instead of building every section from scratch, AI assists with
                                        layout, content structure, page flow, and business-ready
                                        presentation.
                                    </p>
                                    <p>
                                        Shriansh Nexus goes beyond simple page generation. It combines
                                        AI-powered website creation, industry-aware layouts, multi-page
                                        output, and a connected CRM approach so businesses can move from
                                        launch to operations in one ecosystem.
                                    </p>
                                    <p>
                                        Whether you run a real estate company, academy, legal practice,
                                        healthcare clinic, or SaaS business, the platform is designed to
                                        create more relevant websites than generic templates while
                                        preparing the foundation for future management workflows.
                                    </p>
                                </div>
                            </GlassCard>

                            <GlassCard>
                                <h3>Why businesses choose this path</h3>
                                <div className="landing-list-block">
                                    {[
                                        "Faster website launch with AI-assisted workflows",
                                        "Better industry fit than generic one-size-fits-all templates",
                                        "A stronger bridge between marketing website and CRM operations",
                                        "Scalable architecture for future modules, admins, and workflows"
                                    ].map((item) => (
                                        <div className="landing-list-row" key={item}>
                                            <CheckCircle2 size={18} className="landing-icon-cyan" />
                                            <p>{item}</p>
                                        </div>
                                    ))}
                                </div>
                            </GlassCard>
                        </div>
                    </div>
                </section>

                <section id="faq" className="landing-section">
                    <div className="landing-container">
                        <SectionHeading
                            eyebrow="FAQ"
                            title="Questions your visitors will ask before signing up"
                            subtitle="Use this section to reduce friction, increase trust, and make the product direction easier to understand."
                        />

                        <div className="landing-faq-grid">
                            {faqs.map((item) => (
                                <GlassCard key={item.q}>
                                    <h3>{item.q}</h3>
                                    <p>{item.a}</p>
                                </GlassCard>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="landing-section landing-section-last">
                    <div className="landing-container">
                        <GlassCard className="landing-cta-box">
                            <div className="landing-cta-content">
                                <div>
                                    <div className="landing-eyebrow left">
                                        <Sparkles size={16} />
                                        <span>Start building today</span>
                                    </div>
                                    <h2 className="landing-left-title">
                                        Launch faster. Look more premium. Grow with a stronger system.
                                    </h2>
                                    <p className="landing-left-subtitle">
                                        Bring your website, client acquisition, and future operations
                                        into one AI-first platform.
                                    </p>
                                </div>

                                <div className="landing-cta-actions">
                                    <a
                                        href="https://admin.shrianshnexus.in/signup?entry=public"
                                        className="landing-btn landing-btn-primary"
                                    >
                                        Start Free
                                        <ArrowRight size={16} />
                                    </a>
                                    <a
                                        href="https://admin.shrianshnexus.in/login?entry=public"
                                        className="landing-btn landing-btn-secondary"
                                    >
                                        Login
                                    </a>
                                </div>
                            </div>
                        </GlassCard>
                    </div>
                </section>
            </main>

            <footer className="landing-footer">
                <div className="landing-container landing-footer-grid">
                    <div>
                        <div className="landing-brand">
                            <div className="landing-brand-logo">
                                <Sparkles size={20} />
                            </div>
                            <div>
                                <div className="landing-brand-title">Shriansh Nexus</div>
                                <div className="landing-brand-subtitle">Built by Shriansh AI</div>
                            </div>
                        </div>

                        <p className="landing-footer-text">
                            An AI-powered website generation and CRM platform designed for
                            industry-ready business growth.
                        </p>
                    </div>

                    <div>
                        <div className="landing-footer-title">Product</div>
                        <div className="landing-footer-links">
                            <a href="#features">Features</a>
                            <a href="#industries">Industries</a>
                            <a href="#how-it-works">How it works</a>
                            <a href="https://admin.shrianshnexus.in/signup?entry=public">Start free</a>
                        </div>
                    </div>

                    <div>
                        <div className="landing-footer-title">Company</div>
                        <div className="landing-footer-links">
                            <a href="https://shriansh.ai">Shriansh AI</a>
                            <a href="https://admin.shrianshnexus.in/login?entry=public">Login</a>
                            <a href="#request-demo">Request demo</a>
                            <a href="mailto:hello@shriansh.ai">Contact</a>
                        </div>
                    </div>

                    <div>
                        <div className="landing-footer-title">SEO pages</div>
                        <div className="landing-footer-links">
                            <a href="/real-estate-website-generator">Real estate website generator</a>
                            <a href="/academy-website-generator">Academy website generator</a>
                            <a href="/healthcare-website-generator">Healthcare website generator</a>
                            <a href="/legal-website-generator">Legal website generator</a>
                        </div>
                    </div>
                </div>

                <div className="landing-footer-bottom">
                    <div className="landing-container landing-footer-bottom-inner">
                        <div>© {new Date().getFullYear()} Shriansh Nexus. All rights reserved.</div>
                        <div>
                            Powered by{" "}
                            <a href="https://shriansh.ai" className="landing-footer-powered-link">
                                Shriansh AI
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}